// Verbale pubblicato di un evento, letto dall'associato invitato (titolare o
// dipendente con la sezione «eventi»). Prende il posto del dialogo di MyEventsPage.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AlertCircle, ArrowLeft, FileX, RefreshCw } from 'lucide-react';
import { useToast } from '../contexts/ToastContext';
import { isAuthenticated } from '../services/otpAuthService';
import { MESSAGGIO_STUDIO_NON_IN_REGOLA, codiceDaRisposta } from '../utils/sessioneAssociato';
import VerbaleLettura from '../components/verbali/VerbaleLettura';
import {
  leggiVerbaleAssociato,
  scaricaPdfVerbaleAssociato,
  type VerbaleLetturaDati,
} from '../services/verbaliLettoriService';

type Stato =
  | { tipo: 'caricamento' }
  | { tipo: 'pronto'; dati: VerbaleLetturaDati }
  | { tipo: 'non_disponibile' }
  | { tipo: 'accesso_chiuso'; messaggio: string }
  | { tipo: 'errore' };

/** 403 del dipendente che «Riprova» non risolve: messaggio dedicato (null = errore generico). */
const MESSAGGI_ACCESSO_CHIUSO: Record<string, string> = {
  STUDIO_NON_IN_REGOLA: MESSAGGIO_STUDIO_NON_IN_REGOLA,
  SEZIONE_NON_ABILITATA: 'La sezione «Eventi» non è abilitata per il tuo accesso: chiedi al titolare dello studio.',
};

const PERCORSO_EVENTI = '/area-riservata/eventi';

const VerbaleEventoPage: React.FC = () => {
  const { eventId = '' } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const [stato, setStato] = useState<Stato>({ tipo: 'caricamento' });
  // Solo l'ultima richiesta, a componente montato, aggiorna la pagina: una risposta
  // arrivata dopo un cambio di evento (o dopo l'uscita) si scarta. Il contatore non
  // si azzera mai, così un id vecchio non coincide con uno nuovo.
  const contatoreRichieste = useRef(0);
  const richiestaAttiva = useRef<number | null>(null);

  const carica = useCallback(async () => {
    const id = ++contatoreRichieste.current;
    richiestaAttiva.current = id;
    const ancoraAttuale = () => richiestaAttiva.current === id;
    setStato({ tipo: 'caricamento' });
    try {
      const dati = await leggiVerbaleAssociato(eventId);
      if (!ancoraAttuale()) return;
      setStato({ tipo: 'pronto', dati });
    } catch (err: any) {
      if (!ancoraAttuale()) return;
      const status = err?.response?.status;
      if (status === 401) {
        toast.error('Sessione scaduta, effettua nuovamente l\'accesso');
        navigate('/accesso-associati');
        return;
      }
      const chiuso = status === 403 ? MESSAGGI_ACCESSO_CHIUSO[codiceDaRisposta(err?.response?.data) || ''] : undefined;
      if (chiuso) {
        setStato({ tipo: 'accesso_chiuso', messaggio: chiuso });
        return;
      }
      setStato(status === 404 ? { tipo: 'non_disponibile' } : { tipo: 'errore' });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- toast cambia a ogni render: aggiungerlo ricaricherebbe il verbale di continuo
  }, [eventId, navigate]);

  useEffect(() => {
    if (!isAuthenticated()) {
      toast.error('Devi effettuare l\'accesso');
      navigate('/accesso-associati');
      return;
    }
    carica();
    return () => {
      richiestaAttiva.current = null;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- come sopra: si carica all'apertura e al cambio di evento
  }, [carica, navigate]);

  const scaricaPdf = useCallback(() => scaricaPdfVerbaleAssociato(eventId), [eventId]);

  const tornaAgliEventi = (
    <button
      type="button"
      onClick={() => navigate(PERCORSO_EVENTI)}
      className="flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
    >
      <ArrowLeft className="w-4 h-4" />
      Torna ai miei eventi
    </button>
  );

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="mb-4">{tornaAgliEventi}</div>

        {stato.tipo === 'caricamento' && (
          <div className="flex flex-col items-center justify-center py-16 gap-3" role="status">
            <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
            <p className="text-sm text-gray-500 dark:text-gray-400">Caricamento del verbale...</p>
          </div>
        )}

        {stato.tipo === 'non_disponibile' && (
          <div className="mx-auto max-w-xl flex flex-col items-center gap-3 rounded-xl border border-gray-200 bg-white p-6 text-center shadow-sm dark:border-gray-700 dark:bg-gray-800">
            <FileX className="w-8 h-8 text-gray-400 dark:text-gray-500" aria-hidden="true" />
            <p className="text-sm text-gray-700 dark:text-gray-300">
              Il verbale non è disponibile: potrebbe essere stato ritirato o non riguardare il tuo studio.
            </p>
            <button
              type="button"
              onClick={() => navigate(PERCORSO_EVENTI)}
              className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Torna ai miei eventi
            </button>
          </div>
        )}

        {stato.tipo === 'accesso_chiuso' && (
          <div className="mx-auto max-w-xl flex flex-col items-center gap-3 rounded-xl border border-amber-200 bg-white p-6 text-center shadow-sm dark:border-amber-900/60 dark:bg-gray-800">
            <AlertCircle className="w-8 h-8 text-amber-500 dark:text-amber-400" aria-hidden="true" />
            <p className="text-sm text-gray-700 dark:text-gray-300">{stato.messaggio}</p>
            <button
              type="button"
              onClick={() => navigate(PERCORSO_EVENTI)}
              className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Torna ai miei eventi
            </button>
          </div>
        )}

        {stato.tipo === 'errore' && (
          <div className="mx-auto max-w-xl flex flex-col items-center gap-3 rounded-xl border border-red-200 bg-white p-6 text-center shadow-sm dark:border-red-900/60 dark:bg-gray-800">
            <AlertCircle className="w-8 h-8 text-red-500 dark:text-red-400" aria-hidden="true" />
            <p className="text-sm text-gray-700 dark:text-gray-300">Errore nel caricamento del verbale</p>
            <button
              type="button"
              onClick={carica}
              className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700 transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
              Riprova
            </button>
          </div>
        )}

        {stato.tipo === 'pronto' && <VerbaleLettura key={eventId} dati={stato.dati} onScaricaPdf={scaricaPdf} />}
      </div>
    </div>
  );
};

export default VerbaleEventoPage;
